import { BLOCK_COLUMN, columns, moveToNextLeafBlock } from '@gitbook/doc-core';
import {
    type Block,
    type Change,
    isBlockRangeSelection,
    TextRange,
    type Value,
} from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';

import { isModA, isModEnter, isShiftTab, isTab, selectAllInContainerFirst } from '../common';

export const plugin: Plugin = {
    onKeyDown,
};

function onKeyDown(event: KeyboardEvent, change: Change): Change | undefined {
    if (isBlockRangeSelection(change.value.selection)) {
        return;
    }

    if (isModEnter(event)) {
        return onModEnter(event, change);
    }

    if (isModA(event)) {
        event.preventDefault();
        return selectAllInContainerFirst(change, getColumnRange);
    }

    if (isTab(event) || isShiftTab(event)) {
        const current = columns.getCurrentColumns(change.value);

        if (!current) {
            return;
        }

        const { column: currentColumn } = current;
        const next = isTab(event)
            ? change.value.document.getNextSibling(currentColumn.key)
            : change.value.document.getPreviousSibling(currentColumn.key);

        if (next && next.object === 'block' && next.type === BLOCK_COLUMN) {
            event.preventDefault();
            event.stopPropagation();
            change.collapseToEndOf(next);
            return change;
        }
    }

    return undefined;
}

function onModEnter(event: KeyboardEvent, change: Change): Change | undefined {
    const { value } = change;

    if (!value.isCollapsed) {
        return;
    }

    const current = columns.getCurrentColumns(change.value);
    if (!current) {
        return;
    }
    event?.preventDefault();
    moveToNextLeafBlock(change, current.columns);
    return change;
}

function getColumnRange(value: Value, currentBlock: Block): TextRange | null {
    const column = value.document.getClosest(
        currentBlock.key,
        (node) => node.object === 'block' && node.type === BLOCK_COLUMN
    );
    if (!column || column.object !== 'block') {
        return null;
    }
    const firstText = column.getFirstText();
    const lastText = column.getLastText();
    if (!firstText || !lastText) {
        return null;
    }

    return TextRange.create().moveToRangeOf(firstText, lastText);
}
